import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ShoppingBag, SlidersHorizontal } from "lucide-react";
import { formatINR } from "../../data/content";
import { getProductImage } from "../../data/images";
import { ImageSlot } from "../ImageSlot";
import { useCart } from "../../context/CartContext";
import { SHOP } from "../../constants/testIds/shop";

// Luxury product card. Shows ONLY name, price, MRP and stock (no material labels).
export const ShopProductCard = ({ product }) => {
  const img = getProductImage(product.image, `${product.name} — handcrafted HESHAURA bracelet`);
  const navigate = useNavigate();
  const { addItem, openCart } = useCart();
  const soldOut = product.stock <= 0;
  const hasVariants = Array.isArray(product.variants) && product.variants.length > 0;
  const saving = product.mrp > product.price ? Math.round(((product.mrp - product.price) / product.mrp) * 100) : 0;

  const handleAdd = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (soldOut) return;
    if (hasVariants) {
      toast("Choose your options first.", { description: product.name });
      navigate(`/product/${product.slug}`);
      return;
    }
    addItem(product, {}, 1);
    toast.success(`${product.name} added to your bag.`);
    openCart();
  };

  return (
    <article
      className="group relative flex h-full flex-col border border-brand-sand bg-brand-surface transition-[transform,box-shadow,border-color] duration-500 ease-out hover:-translate-y-1.5 hover:border-brand-gold/50 hover:shadow-[0_30px_50px_-30px_rgba(28,25,23,0.35)]"
      data-testid={SHOP.card}
    >
      <Link
        to={`/product/${product.slug}`}
        data-testid={SHOP.cardLink}
        className="flex h-full flex-col"
        aria-label={product.name}
      >
        <div className="relative overflow-hidden">
          <ImageSlot
            name={`product-${product.slug}`}
            label={product.name}
            src={img.src}
            alt={img.alt}
            ratio="aspect-square"
            fit="contain"
            imgClassName="p-5 sm:p-7 transition-transform duration-700 ease-out group-hover:scale-[1.06]"
          />
          {soldOut && (
            <span className="absolute left-3 top-3 bg-brand-charcoal px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-brand-cream">
              Sold Out
            </span>
          )}
          {!soldOut && product.flags?.bestseller && (
            <span className="absolute left-3 top-3 bg-brand-charcoal px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-brand-gold-light">
              Bestseller
            </span>
          )}
          {!soldOut && !product.flags?.bestseller && product.flags?.newArrival && (
            <span className="absolute left-3 top-3 border border-brand-gold/60 bg-brand-cream px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-brand-gold">
              New
            </span>
          )}
          {!soldOut && saving > 0 && (
            <span className="absolute right-3 top-3 bg-brand-charcoal px-2 py-1 text-[10px] font-semibold tracking-[0.06em] text-brand-cream shadow-[0_6px_16px_-8px_rgba(28,25,23,0.6)]">
              −{saving}%
            </span>
          )}
          <div className="absolute inset-x-0 bottom-0 translate-y-full transition-transform duration-300 ease-out group-hover:translate-y-0">
            <button
              type="button"
              onClick={handleAdd}
              disabled={soldOut}
              data-testid={SHOP.cardAdd}
              aria-label={hasVariants ? `Choose options for ${product.name}` : `Add ${product.name} to bag`}
              className="flex w-full items-center justify-center gap-2 bg-brand-charcoal/95 py-3.5 text-[11px] font-semibold uppercase tracking-[0.2em] text-brand-cream transition-colors duration-300 hover:bg-brand-gold disabled:cursor-not-allowed disabled:opacity-40"
            >
              {soldOut ? (
                "Sold Out"
              ) : hasVariants ? (
                <>
                  <SlidersHorizontal className="h-3.5 w-3.5" /> Choose Options
                </>
              ) : (
                <>
                  <ShoppingBag className="h-3.5 w-3.5" /> Add to Bag
                </>
              )}
            </button>
          </div>
        </div>
        <div className="flex flex-1 flex-col border-t border-brand-sand/70 px-4 pb-5 pt-4 sm:px-5 sm:pb-6">
          <h3 className="line-clamp-2 min-h-[3.25rem] font-serif text-lg font-medium leading-snug text-brand-charcoal transition-colors duration-300 group-hover:text-brand-gold sm:min-h-[3.5rem] sm:text-xl">
            {product.name}
          </h3>
          <div className="mt-2.5 flex flex-wrap items-baseline gap-x-2 gap-y-1">
            <span className="text-sm font-semibold text-brand-charcoal">{formatINR(product.price)}</span>
            {product.mrp > product.price && (
              <>
                <span className="text-xs text-brand-stone/80 line-through">{formatINR(product.mrp)}</span>
                <span className="text-[10px] font-semibold uppercase tracking-[0.15em] text-brand-gold">{saving}% off</span>
              </>
            )}
          </div>
          <p
            className={`mt-auto flex items-center gap-1.5 pt-3 text-[10px] font-medium uppercase tracking-[0.18em] ${
              !soldOut && product.stock <= 8 ? "text-brand-gold" : "text-brand-stone"
            }`}
          >
            <span
              className={`h-1.5 w-1.5 rounded-full ${
                soldOut ? "bg-brand-stone/50" : product.stock <= 8 ? "bg-brand-gold" : "bg-emerald-600/70"
              }`}
              aria-hidden="true"
            />
            {soldOut ? "Out of stock" : product.stock <= 8 ? `Only ${product.stock} left` : "In stock · Ships in 2–4 days"}
          </p>
        </div>
      </Link>
    </article>
  );
};
